import { useState } from "react";
import { Plus, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const DynamicSelect = ({ field, value, onChange }) => {
  const { options, loadOptions, user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const isAdmin = user?.role === "admin";

  const base = field.category ? options[field.category] || [] : field.options || [];
  // keep a stored legacy value visible even if it is no longer in the option master
  const list = value && !base.includes(value) ? [value, ...base] : base;

  const addOption = async () => {
    const val = draft.trim();
    if (!val) return;
    try {
      if (isAdmin) {
        await api.post("/options", { category: field.category, value: val });
        await loadOptions();
        onChange(val);
        toast.success(`"${val}" added to ${field.label} options`);
      } else {
        const { data } = await api.post("/option-requests", {
          category: field.category, value: val,
          reason: `Needed while filling ${field.label}`,
        });
        if (data?.already_requested) {
          toast.info(`"${val}" was already requested and is awaiting approval`);
        } else {
          toast.success(`Requested "${val}" — an administrator will approve it shortly`);
        }
      }
      setDraft("");
      setAdding(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (adding) {
    return (
      <div className="mt-1.5">
        <div className="flex gap-1.5">
          <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={`New ${field.label}`} data-testid={`new-option-input-${field.name}`}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())} />
          <button type="button" onClick={addOption} data-testid={`save-option-${field.name}`}
            className="px-2 rounded-md bg-[#002FA7] text-white"><Check className="h-4 w-4" /></button>
          <button type="button" onClick={() => setAdding(false)} data-testid={`cancel-option-${field.name}`}
            className="px-2 rounded-md border border-[#E5E7EB]"><X className="h-4 w-4" /></button>
        </div>
        {!isAdmin && (
          <p className="text-[11px] text-[#6B7280] mt-1" data-testid={`option-request-hint-${field.name}`}>
            This will be sent to an administrator for approval.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-1.5 mt-1.5 min-w-0">
      {/* remount when a legacy value is injected so the trigger picks up its label */}
      <Select key={value && !base.includes(value) ? `legacy-${value}` : "master"}
        value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`select-${field.name}`} className="bg-white min-w-0 flex-1">
          <SelectValue placeholder={`Select ${field.label}`} />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {list.length === 0 && <div className="px-3 py-2 text-xs text-[#6B7280]">No options yet</div>}
          {list.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {field.category && (
        <button type="button" onClick={() => setAdding(true)} data-testid={`add-option-${field.name}`}
          title={isAdmin ? "Add new option" : "Request a new option"}
          className="shrink-0 w-9 flex items-center justify-center rounded-md border border-[#E5E7EB]
            hover:border-[#002FA7] hover:text-[#002FA7]">
          <Plus className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
